if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();

        this.hasWholesaleAccess = window.gfdWholesaleCustomer === true;
        this.lastActiveInputId = null;
        this.wholesaleObserver = null;
        this.isBlockingWholesaleContent = false;

        if (!this.hasWholesaleAccess) {
          this.blockWholesaleAccess();
          return;
        }

        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          const quantity = parseInt(event.target.value, 10);

          if (quantity === 0) {
            this.startQueue(event.target.dataset.index, quantity);
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
      }

      connectedCallback() {
        if (!this.hasWholesaleAccess) {
          this.startWholesaleProtection();
          this.blockWholesaleAccess();
          return;
        }

        this.cartUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (
              event.source === 'quick-add' ||
              (
                event.cartData.items &&
                !event.cartData.items.some(
                  (item) =>
                    item.id === parseInt(this.dataset.index, 10)
                )
              ) ||
              (
                event.cartData.variant_id &&
                event.cartData.variant_id !==
                  parseInt(this.dataset.index, 10)
              )
            ) {
              return;
            }

            this.onCartUpdate().then(() => {
              this.listenForActiveInput();
              this.listenForKeydown();
            });
          }
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.wholesaleObserver) {
          this.wholesaleObserver.disconnect();
          this.wholesaleObserver = null;
        }
      }

      startWholesaleProtection() {
        if (this.wholesaleObserver) return;

        this.wholesaleObserver = new MutationObserver(() => {
          if (
            !this.hasWholesaleAccess &&
            !this.isBlockingWholesaleContent
          ) {
            this.blockWholesaleAccess();
          }
        });

        this.wholesaleObserver.observe(this, {
          childList: true,
          subtree: true,
        });
      }

      blockWholesaleAccess() {
        if (this.hasWholesaleAccess) return;
        if (this.isBlockingWholesaleContent) return;

        const existingMessage = this.querySelector(
          '.wholesale-price-message'
        );

        if (
          existingMessage &&
          this.children.length === 1
        ) {
          return;
        }

        this.isBlockingWholesaleContent = true;

        this.innerHTML = `
          <div
            class="wholesale-price-message"
            role="status"
            aria-live="polite"
          >
            <a href="/account/login">
              Please log in to your vendor account to see pricing.
            </a>
          </div>
        `;

        this.isBlockingWholesaleContent = false;
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.hasWholesaleAccess) return;

        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => {
            event.target.select();
          });
        }

        this.isEnterPressed = false;
      }

      listenForKeydown() {
        if (!this.hasWholesaleAccess) return;

        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId =
            this.closest('.collection-quick-add-bulk')?.dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        if (!this.hasWholesaleAccess) {
          this.blockWholesaleAccess();
          return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
          fetch(
            `${this.getSectionsUrl()}?section_id=${this.sectionId}`
          )
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

              const sourceQty = html.querySelector(
                `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`
              );

              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }

              resolve();
            })
            .catch((error) => {
              console.error(error);
              reject(error);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(
          window.location.search
        );

        const pageNumber = decodeURIComponent(
          pageParams.get('page') || ''
        );

        return `${window.location.pathname}${
          pageNumber ? `?page=${pageNumber}` : ''
        }`;
      }

      updateMultipleQty(items) {
        if (!this.hasWholesaleAccess) {
          this.blockWholesaleAccess();
          return;
        }

        this.selectProgressBar()?.classList.remove('hidden');

        const ids = Object.keys(items);
        const linesUpdate = this.startCartLinesUpdate(items);

        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map(
            (section) => section.section
          ),
          sections_url: this.getSectionsUrl(),
        });

        fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body,
        })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.errors) {
              throw Object.assign(
                new Error(parsedState.errors),
                {
                  code: 'INVALID',
                }
              );
            }

            linesUpdate?.resolve(parsedState);
            this.renderSections(parsedState, ids);

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-add',
              cartData: parsedState,
            });
          })
          .catch((error) => {
            if (error.code !== 'INVALID') {
              console.error(error);
            }

            this.dispatchCartErrorEvent(
              error.code === 'INVALID'
                ? error.message
                : window.cartStrings.error,
              error.code || 'SERVICE_UNAVAILABLE'
            );

            linesUpdate?.reject(error);
          })
          .finally(() => {
            this.selectProgressBar()?.classList.add('hidden');
            this.setRequestStarted(false);
          });
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector:
              `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        if (!this.hasWholesaleAccess) {
          this.blockWholesaleAccess();
          return;
        }

        const intersection = this.queue.filter((element) =>
          ids.includes(element.id)
        );

        if (intersection.length !== 0) return;

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(
            section.id
          );

          if (!sectionElement) return;

          if (section.section === 'cart-drawer') {
            sectionElement
              .closest('cart-drawer')
              ?.classList.toggle(
                'is-empty',
                parsedState.items.length === 0
              );
          }

          const elementToReplace =
            sectionElement.querySelector(section.selector) ||
            sectionElement;

          if (
            elementToReplace &&
            parsedState.sections?.[section.section]
          ) {
            elementToReplace.innerHTML =
              this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
          }
        });

        if (
          this.isEnterPressed &&
          this.lastActiveInputId
        ) {
          this.querySelector(
            `#Quantity-${this.lastActiveInputId}`
          )?.select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}